// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * runner.ts — one live round: the loop, the HUD, the juice, the opponents.
 *
 * Owns nothing authoritative. In multiplayer every peer runs its OWN well from
 * the same frozen (seed, mode, roster), so this file never receives board
 * state — only attacks in, and status/attacks out. That is what makes the whole
 * netcode desync-proof and host transfer a display concern.
 */

import { createSfx, type Sfx } from '@ben-gy/game-engine/sound';
import { Game } from './game';
import { BOT_PRESETS, botPlay } from './bot';
import { createRenderer, type Renderer } from './render';
import { attachTouch, type TouchLayer } from './touch';
import { attachKeys, type KeyLayer } from './keys';
import { MARKS, PIECE_COLORS } from './palette';
import { cellsAt } from './pieces';
import { el, fmt, setHidden } from './dom';
import { TRIM_BAND } from './game';
import type { Mode } from './modes';

export interface PlayerResult {
  id: string;
  name: string;
  score: number;
  lines: number;
  level: number;
  pieces: number;
  /** Placements made while the well was in trim — "did you sail it well". */
  trimmed: number;
  maxCombo: number;
  quads: number;
  ballastSent: number;
  ballastReceived: number;
  survivedMs: number;
  isSelf: boolean;
  isBot: boolean;
  left: boolean;
}

export interface OpponentSpec {
  id: string;
  name: string;
  /** Present for a local bot; absent for a remote peer. */
  bot?: keyof typeof BOT_PRESETS;
}

export interface RunnerOpts {
  stage: HTMLElement;
  mode: Mode;
  seed: number;
  selfName: string;
  selfId: string;
  reducedMotion: boolean;
  sfx: Sfx;
  opponents: OpponentSpec[];
  /** Multiplayer: this peer cleared lines and owes ballast. */
  onAttack?: (n: number) => void;
  /** Multiplayer: gossip our well ~4Hz so opponents' mini-wells stay live. */
  onStatus?: (st: { lines: number; level: number; list: number; alive: boolean; cols: number[] }) => void;
  onSelfEnd?: (r: PlayerResult) => void;
  /** Every well is finished (or the round was force-ended). */
  onRoundEnd: (results: PlayerResult[]) => void;
}

interface Well {
  id: string;
  name: string;
  game: Game | null;
  bot: keyof typeof BOT_PRESETS | null;
  isSelf: boolean;
  left: boolean;
  /** Remote peers report these; local wells compute them. */
  lines: number;
  level: number;
  list: number;
  alive: boolean;
  cols: number[];
  survivedMs: number;
  botAcc: number;
  canvas?: HTMLCanvasElement;
  nameEl?: HTMLElement;
  statEl?: HTMLElement;
}

export interface Runner {
  destroy(): void;
  pause(): void;
  resume(): void;
  togglePause(): void;
  paused(): boolean;
  /** Multiplayer: ballast arriving from a peer. */
  receiveAttack(n: number): void;
  /** Multiplayer: a peer's gossiped well. */
  applyStatus(id: string, st: { lines: number; level: number; list: number; alive: boolean; cols: number[] }): void;
  /** A peer left — its well stops, the round does not. */
  markLeft(id: string): void;
  /** Force the round to finish now (host said so, or everyone else is done). */
  finishNow(): void;
  results(): PlayerResult[];
  /** For tests: step the sim without a rAF, which a hidden tab never fires. */
  stepForTest(dtMs: number): void;
  /** The live well — for the in-browser verification pass only. */
  debugGame(): Game;
}

/** How long a bot waits between placements, so the race is watchable. */
const BOT_STEP_MS = 620;

export function createRunner(opts: RunnerOpts): Runner {
  const { stage, mode, seed, sfx } = opts;

  // ------------------------------------------------------------------ DOM ---
  stage.innerHTML = '';
  const wrap = el('div', 'play');
  const boardBox = el('div', 'board-box');
  const canvas = el('canvas', 'board-canvas');
  canvas.setAttribute('aria-label', 'The well');
  boardBox.appendChild(canvas);

  const hud = el('div', 'hud');
  const hudTop = el('div', 'hud-top');
  const scoreEl = el('div', 'hud-stat', '<span class="hud-k">Score</span><span class="hud-v" id="v-score">0</span>');
  const linesEl = el('div', 'hud-stat', '<span class="hud-k">Lines</span><span class="hud-v" id="v-lines">0</span>');
  const levelEl = el('div', 'hud-stat', '<span class="hud-k">Level</span><span class="hud-v" id="v-level">1</span>');
  hudTop.append(scoreEl, linesEl, levelEl);

  const listBox = el('div', 'listmeter');
  listBox.innerHTML = `
    <div class="lm-track" aria-hidden="true">
      <div class="lm-band"></div>
      <div class="lm-needle"></div>
    </div>
    <div class="lm-labels"><span>port</span><span class="lm-pill" id="v-trim">IN TRIM</span><span>starboard</span></div>`;

  const slots = el('div', 'slots');
  const holdSlot = el('div', 'slot', '<span class="slot-k">Hold</span><canvas class="slot-c" width="76" height="52"></canvas>');
  const nextSlot = el('div', 'slot', '<span class="slot-k">Next</span><canvas class="slot-c" width="76" height="132"></canvas>');
  slots.append(holdSlot, nextSlot);

  const oppRail = el('div', 'opp-rail');
  setHidden(oppRail, opts.opponents.length === 0);

  const pauseBtn = el('button', 'icon-btn');
  pauseBtn.type = 'button';
  pauseBtn.textContent = 'Pause';
  pauseBtn.setAttribute('aria-label', 'Pause');

  hud.append(hudTop, listBox, slots, oppRail, pauseBtn);
  wrap.append(boardBox, hud);

  const overlay = el('div', 'game-overlay');
  overlay.setAttribute('hidden', '');
  overlay.innerHTML = '<div class="go-card"><h2 class="go-title">Paused</h2><p class="go-sub"></p></div>';
  const resumeBtn = el('button', 'btn');
  resumeBtn.type = 'button';
  resumeBtn.textContent = 'Resume';
  overlay.querySelector('.go-card')!.appendChild(resumeBtn);
  boardBox.appendChild(overlay);

  stage.appendChild(wrap);

  const vScore = wrap.querySelector('#v-score') as HTMLElement;
  const vLines = wrap.querySelector('#v-lines') as HTMLElement;
  const vLevel = wrap.querySelector('#v-level') as HTMLElement;
  const vTrim = wrap.querySelector('#v-trim') as HTMLElement;
  const needle = wrap.querySelector('.lm-needle') as HTMLElement;
  const holdCanvas = holdSlot.querySelector('canvas') as HTMLCanvasElement;
  const nextCanvas = nextSlot.querySelector('canvas') as HTMLCanvasElement;

  // ----------------------------------------------------------------- state ---
  const renderer: Renderer = createRenderer(canvas);
  renderer.setReducedMotion(opts.reducedMotion);
  const touch: TouchLayer = attachTouch(boardBox);
  const keys: KeyLayer = attachKeys(window);

  const game = new Game({ mode, seed });
  const self: Well = {
    id: opts.selfId, name: opts.selfName, game, bot: null, isSelf: true, left: false,
    lines: 0, level: 1, list: 0, alive: true, cols: game.columnHeights(),
    survivedMs: 0, botAcc: 0,
  };
  const wells: Well[] = [self];
  for (const o of opts.opponents) {
    wells.push({
      id: o.id, name: o.name,
      game: o.bot ? new Game({ mode, seed }) : null,
      bot: o.bot ?? null,
      isSelf: false, left: false,
      lines: 0, level: 1, list: 0, alive: true,
      cols: new Array(mode.cols).fill(0),
      survivedMs: 0, botAcc: 0,
    });
  }

  for (const w of wells) {
    if (w.isSelf) continue;
    const card = el('div', 'opp');
    const c = el('canvas', 'opp-canvas');
    c.width = 54;
    c.height = 78;
    const nm = el('div', 'opp-name');
    nm.textContent = w.name;
    const st = el('div', 'opp-stat');
    st.textContent = '0';
    card.append(c, nm, st);
    oppRail.appendChild(card);
    w.canvas = c;
    w.nameEl = nm;
    w.statEl = st;
  }

  let paused = false;
  let ended = false;
  let raf = 0;
  let last = 0;
  const nowMs = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
  let lastTickAt = nowMs();
  let statusAcc = 0;
  let flashRows: number[] = [];
  let flash = 0;
  let comboToast: { text: string; life: number } | null = null;
  /** One explanation per round when the well first jams a piece against a wall. */
  let warnedJam = false;

  const toast = el('div', 'toast');
  boardBox.appendChild(toast);

  document.body.classList.add('playing');

  // ------------------------------------------------------------- rendering ---
  function drawMini(w: Well): void {
    const c = w.canvas;
    if (!c) return;
    const g = c.getContext('2d');
    if (!g) return;
    g.clearRect(0, 0, c.width, c.height);
    g.fillStyle = '#0f1a26';
    g.fillRect(0, 0, c.width, c.height);
    const cw = c.width / mode.cols;
    const ch = c.height / mode.rows;
    const heights = w.game ? w.game.columnHeights() : w.cols;
    g.fillStyle = w.alive && !w.left ? '#5bb8f2' : '#7f96ad';
    g.globalAlpha = w.left ? 0.35 : 0.9;
    for (let i = 0; i < mode.cols; i++) {
      const h = Math.min(mode.rows, heights[i] ?? 0);
      if (h <= 0) continue;
      g.fillRect(i * cw, c.height - h * ch, Math.max(1, cw - 0.6), h * ch);
    }
    g.globalAlpha = 1;
    const list = w.game ? w.game.list() : w.list;
    g.strokeStyle = Math.abs(list) < TRIM_BAND ? MARKS.trim : MARKS.list;
    g.lineWidth = 1.5;
    g.beginPath();
    const mid = c.width / 2;
    g.moveTo(mid - 9, 4 + list * 4);
    g.lineTo(mid + 9, 4 - list * 4);
    g.stroke();
    if (w.statEl) w.statEl.textContent = String(w.game ? w.game.lines : w.lines);
    if (w.nameEl) w.nameEl.classList.toggle('is-left', w.left);
  }

  function drawPreview(cv: HTMLCanvasElement, types: number[]): void {
    const g = cv.getContext('2d');
    if (!g) return;
    g.clearRect(0, 0, cv.width, cv.height);
    const slotH = cv.height / Math.max(1, types.length);
    types.forEach((t, i) => {
      const cells = cellsAt(t, 0, 0, 0);
      const xs = cells.map(([x]) => x);
      const ys = cells.map(([, y]) => y);
      const w = Math.max(...xs) - Math.min(...xs) + 1;
      const h = Math.max(...ys) - Math.min(...ys) + 1;
      const cs = Math.min(11, (cv.width - 10) / w, (slotH - 8) / h);
      const ox = (cv.width - w * cs) / 2 - Math.min(...xs) * cs;
      const oy = i * slotH + (slotH - h * cs) / 2 - Math.min(...ys) * cs;
      g.fillStyle = PIECE_COLORS[t];
      for (const [x, y] of cells) g.fillRect(ox + x * cs + 1, oy + y * cs + 1, cs - 2, cs - 2);
    });
  }

  function paintHud(): void {
    vScore.textContent = fmt(game.score);
    vLines.textContent = String(game.lines);
    vLevel.textContent = String(game.level);
    const list = game.list();
    // `left`, not translateX: a percentage in `transform` resolves against the
    // ELEMENT's width (4px here), so translateX(-13%) moved the needle half a
    // pixel and the meter read as decoration. `left` resolves against the track.
    needle.style.left = `calc(${(50 + list * 50).toFixed(2)}% - 2px)`;
    const inTrim = Math.abs(list) < TRIM_BAND;
    vTrim.classList.toggle('on', inTrim);
    vTrim.textContent = inTrim ? 'IN TRIM' : `LISTING ${list > 0 ? '▸' : '◂'}`;
    drawPreview(nextCanvas, game.peek(3));
    drawPreview(holdCanvas, game.hold === null ? [] : [game.hold]);
  }

  // ------------------------------------------------------------------ juice ---
  function consumeEvents(): void {
    for (const e of game.drainEvents()) {
      switch (e.t) {
        case 'lock':
          sfx.play('blip');
          break;
        case 'drift':
          if (e.blocked) {
            // The well tried to slide the piece downhill and something stopped
            // it — nearly always the wall, because a leaning player is already
            // hugging it. Silent, this is the single most common way the whole
            // mechanic goes unnoticed: 62% of drifts are blocked for a player
            // piling to one side (84% in Careen), so the piece lands exactly
            // where they aimed and nothing appears to happen. Make it a jam
            // you can feel.
            renderer.shake(3);
            sfx.play('hit');
            if (!warnedJam) {
              warnedJam = true;
              comboToast = { text: 'Listing hard — the well is pinning you to the wall', life: 1800 };
            }
          } else {
            renderer.shake(1.5);
          }
          break;
        case 'rotate':
          sfx.play('select');
          break;
        case 'harddrop':
          if (e.rows > 0) { renderer.shake(2 + e.rows * 0.35); sfx.play('hit'); }
          break;
        case 'clear': {
          flashRows = e.rows;
          flash = 1;
          const cells: [number, number][] = [];
          for (const r of e.rows) for (let c = 0; c < game.cols; c++) cells.push([c, r]);
          renderer.burst(game, cells, e.inTrim ? MARKS.trim : MARKS.pop);
          renderer.shake(4 + e.n * 3);
          sfx.play(e.n >= 4 ? 'explosion' : 'coin');
          if (e.inTrim) sfx.play('powerup');
          comboToast = {
            text: `${e.n === 4 ? 'QUAD' : `${e.n} row${e.n > 1 ? 's' : ''}`}${e.inTrim ? ' · IN TRIM ×2' : ''}  +${fmt(e.score)}`,
            life: 1100,
          };
          break;
        }
        case 'ballast':
          renderer.shake(7);
          sfx.play('hit');
          comboToast = { text: `Ballast aboard ×${e.n}`, life: 900 };
          break;
        case 'attack':
          opts.onAttack?.(e.n);
          break;
        case 'levelup':
          sfx.play('powerup');
          break;
        case 'over':
          sfx.play('lose');
          break;
        default:
          break;
      }
    }
  }

  // ------------------------------------------------------------------- loop ---
  function applyInput(dtMs: number): void {
    keys.tick(dtMs);
    const k = keys.drain();
    const t = touch.drain();
    if (k.pause) { togglePause(); return; }
    if (paused || game.over) return;

    const moves = k.moves + t.moves;
    const step = Math.sign(moves);
    for (let i = 0; i < Math.min(Math.abs(moves), 24); i++) game.move(step);
    for (let i = 0; i < k.rotates + t.rotates; i++) game.rotate(1);
    for (let i = 0; i < k.rotatesCcw; i++) game.rotate(-1);
    for (let i = 0; i < Math.min(k.soft + t.soft, 30); i++) if (!game.softDrop()) break;
    if (k.hold || t.hold) game.holdPiece();
    if (k.hard || t.hard) game.hardDrop();
  }

  function stepBots(dtMs: number): void {
    for (const w of wells) {
      if (w.isSelf || !w.game || !w.bot || w.left) continue;
      if (w.game.over) { w.alive = false; continue; }
      w.botAcc += dtMs;
      w.survivedMs += dtMs;
      while (w.botAcc >= BOT_STEP_MS) {
        w.botAcc -= BOT_STEP_MS;
        const before = w.game.ballastSent;
        if (!botPlay(w.game, BOT_PRESETS[w.bot], Math.random)) { w.alive = false; break; }
        const sent = w.game.ballastSent - before;
        // A bot's ballast goes to the current line leader, exactly as the
        // networked rule does — including possibly back at another bot.
        if (sent > 0) routeAttack(w.id, sent);
        w.game.drainEvents();
        if (w.game.over) { w.alive = false; break; }
      }
    }
  }

  function routeAttack(fromId: string, n: number): void {
    const live = wells.filter((w) => w.id !== fromId && !w.left && aliveOf(w));
    if (live.length === 0) return;
    let target = live[0];
    for (const w of live) if (linesOf(w) > linesOf(target)) target = w;
    if (target.isSelf) game.receiveBallast(n);
    else if (target.game) target.game.receiveBallast(n);
  }

  const aliveOf = (w: Well) => (w.game ? !w.game.over : w.alive);
  const linesOf = (w: Well) => (w.game ? w.game.lines : w.lines);

  /**
   * ONE frame. Both the rAF loop and the external step hook call this, so a
   * frame driven from a test is the same code path a frame driven by the
   * browser is — including render and HUD, not just the sim.
   */
  function tick(dt: number): void {
    if (ended) return;
    lastTickAt = nowMs();

    applyInput(dt);
    if (!paused && !game.over) {
      game.step(dt);
      self.survivedMs += dt;
      stepBots(dt);
    }
    consumeEvents();

    if (flash > 0) flash -= dt / 150;
    renderer.draw(game, dt, { flashRows, flash, paused });
    paintHud();
    for (const w of wells) if (!w.isSelf) drawMini(w);

    if (comboToast) {
      comboToast.life -= dt;
      if (comboToast.life <= 0) { comboToast = null; toast.textContent = ''; toast.classList.remove('show'); }
      else { toast.textContent = comboToast.text; toast.classList.add('show'); }
    }

    statusAcc += dt;
    if (statusAcc >= 250) {
      statusAcc = 0;
      opts.onStatus?.({
        lines: game.lines, level: game.level, list: game.list(),
        alive: !game.over, cols: game.columnHeights(),
      });
    }

    if (game.over && !ended) maybeFinish();
  }

  function frame(now: number): void {
    raf = requestAnimationFrame(frame);
    const dt = Math.min(64, last ? now - last : 16);
    last = now;
    tick(dt);
  }

  /**
   * rAF is not a guarantee. A hidden tab stops it (which is what we WANT — you
   * should not top out while you are reading something else), but some mobile
   * browsers also starve it while the page is genuinely on screen. This backstop
   * notices a visible page whose frames have stalled and steps it manually, so
   * the round cannot silently freeze under the player.
   */
  const backstop = window.setInterval(() => {
    if (ended || paused || document.hidden) return;
    const gap = nowMs() - lastTickAt;
    if (gap > 400) { last = 0; tick(Math.min(64, gap)); }
  }, 250);

  let selfReported = false;
  function maybeFinish(): void {
    if (!selfReported) {
      selfReported = true;
      self.alive = false;
      opts.onSelfEnd?.(resultOf(self));
    }
    // Solo and bot races end when every local well is done. In a networked
    // round the session decides, because a remote well is not ours to judge.
    const localOnly = wells.every((w) => w.isSelf || w.game !== null);
    if (localOnly && wells.every((w) => !aliveOf(w) || w.left)) finishNow();
  }

  function resultOf(w: Well): PlayerResult {
    const g = w.game;
    return {
      id: w.id,
      name: w.name,
      score: g ? g.score : 0,
      lines: g ? g.lines : w.lines,
      level: g ? g.level : w.level,
      pieces: g ? g.pieces : 0,
      trimmed: g ? g.trimmed : 0,
      maxCombo: g ? g.maxCombo : 0,
      quads: g ? g.quads : 0,
      ballastSent: g ? g.ballastSent : 0,
      ballastReceived: g ? g.ballastReceived : 0,
      survivedMs: Math.round(w.survivedMs),
      isSelf: w.isSelf,
      isBot: w.bot !== null,
      left: w.left,
    };
  }

  function finishNow(): void {
    if (ended) return;
    ended = true;
    document.body.classList.remove('playing');
    opts.onRoundEnd(wells.map(resultOf));
  }

  function togglePause(): void {
    if (ended || game.over) return;
    paused = !paused;
    setHidden(overlay, !paused);
    if (!paused) last = 0;
  }

  pauseBtn.addEventListener('click', () => togglePause());
  resumeBtn.addEventListener('click', () => togglePause());

  const onVisibility = () => { if (document.hidden && !paused) togglePause(); };
  document.addEventListener('visibilitychange', onVisibility);
  const onResize = () => renderer.resize();
  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);

  renderer.resize();
  paintHud();
  for (const w of wells) if (!w.isSelf) drawMini(w);
  raf = requestAnimationFrame(frame);

  return {
    destroy() {
      ended = true;
      cancelAnimationFrame(raf);
      clearInterval(backstop);
      touch.destroy();
      keys.destroy();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      document.body.classList.remove('playing');
    },
    pause() { if (!paused) togglePause(); },
    resume() { if (paused) togglePause(); },
    togglePause,
    paused: () => paused,
    receiveAttack(n) { game.receiveBallast(n); },
    applyStatus(id, st) {
      const w = wells.find((x) => x.id === id);
      if (!w || w.isSelf || w.game) return;
      w.lines = st.lines;
      w.level = st.level;
      w.list = st.list;
      w.alive = st.alive;
      if (Array.isArray(st.cols)) w.cols = st.cols;
      drawMini(w);
    },
    markLeft(id) {
      const w = wells.find((x) => x.id === id);
      if (!w) return;
      w.left = true;
      w.alive = false;
      drawMini(w);
      if (game.over) maybeFinish();
    },
    finishNow,
    results: () => wells.map(resultOf),
    stepForTest(dtMs) { tick(dtMs); },
    debugGame: () => game,
  };
}

/** Convenience for callers that just want a muted default. */
export const makeSfx = createSfx;
